import { Button, Col, Input, InputNumber, Modal, Row, Table, Typography } from "antd";
import { useMemo } from "react";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { DISPLAY_SAMPLING_RESULT, ReportStatusEnum } from "src/entity/common/common.constant";
import { convertEmissionWord } from "src/entity/common/global.entity";
import { MONTHS_LISTS } from "src/entity/common/time.constant";
import { UnitAllEnum } from "src/entity/common/unit.constant";
import {
  OthersEmission,
  ResponseOthersFillSamplingResult,
  FillEmissionObject,
  UpdateOthersFillEmission,
  FillSamplingObject,
} from "src/entity/others/fill-emission";
import { onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";
import { capitalizeFirstLetter } from "src/utils/manage-string";
import { formatNumber } from "src/utils/process-number";
import "./FillSamplingResultModal.css";

const { Text } = Typography;

interface FillSamplingResultModalProps {
  dataEmission: UpdateOthersFillEmission;
  submitFillEmission: () => void;
  loadingManageFillEmission: boolean;
  visibleModal: boolean;
  closeModal: () => void;
  errorFillEmission: string | null;
  dataUpdate: ResponseOthersFillSamplingResult[] | null;
  getMasterLabel: (type: string, text: string) => string | undefined;
  onUpdateKeyValue: (key: keyof OthersEmission, samplingResult: string, value: string | number, month: string) => void;
}

const FillSamplingResultModal: React.FC<FillSamplingResultModalProps> = (props) => {
  const {
    dataEmission,
    submitFillEmission,
    loadingManageFillEmission,
    closeModal,
    visibleModal,
    errorFillEmission,
    dataUpdate,
    getMasterLabel,
    onUpdateKeyValue,
  } = props;

  const dataDisplay = useMemo(() => {
    return dataUpdate;
  }, [dataUpdate]);

  const getColumns = () => {
    return MONTHS_LISTS.map((month) => ({
      title: capitalizeFirstLetter(month),
      key: month,
      align: "center" as const,
      render: (_: any, record: any) => {
        const value =
          dataEmission?.emission?.[record.activeEmission.toLowerCase() as keyof OthersEmission]?.fillSamplingResult?.[
            record.samplingResult as keyof FillSamplingObject
          ]?.[month as keyof FillEmissionObject] ??
          record[month] ??
          0;

        return (
          <InputNumber
            key={`${record._id}-${record.activeEmission}-${record.samplingResult}-${month}`}
            onChange={(val) => {
              console.log("InputNumber onChange:", { month, val, record: record.activeEmission });
              onUpdateKeyValue(record.activeEmission as keyof OthersEmission, record.samplingResult, val ?? 0, month);
            }}
            className="w-16"
            value={value}
            disabled={
              record?.vocEmission &&
              record?.vocEmission[record.activeEmission.toLowerCase()]?.[month as keyof FillEmissionObject]?.reportStatus !==
                ReportStatusEnum.PROCESSING
            }
            onKeyDown={(e) => {
              onKeyDownNumberWithFixedDecimal(e, 2);
            }}
            formatter={(value) => formatNumber(value as number, 2)}
          />
        );
      },
    }));
  };

  const getRowClassName = (record: any, index: number) => {
    if (index === 0) return "";

    const prevRecord = (dataDisplay as any)[index - 1];
    if (prevRecord && prevRecord.activeEmission !== record.activeEmission) {
      return "border-t-2 border-gray-300";
    }

    return "";
  };

  const columns = useMemo(
    () => [
      {
        title: "",
        dataIndex: "activeEmission",
        render: (text: any, record: any, index: number) => {
          const obj: any = {
            children: <Text>{convertEmissionWord(text)}</Text>,
            props: {
              rowSpan: 1,
              className: "",
            },
          };

          if (
            index === 0 ||
            (dataDisplay as any)[index - 1]._id !== record._id ||
            (dataDisplay as any)[index - 1].activeEmission !== record.activeEmission
          ) {
            const groupLength = (dataDisplay as any).filter(
              (item: any) => item._id === record._id && item.activeEmission === record.activeEmission,
            ).length;
            obj.props.rowSpan = groupLength;

            // obj.props.className = "border-bottom-active-emission";
          } else {
            obj.props.rowSpan = 0;
          }

          return obj;
        },
      },
      {
        title: "Sampling Result",
        dataIndex: "samplingResult",
        key: "samplingResult",
        render: (text: any) => <Text>{DISPLAY_SAMPLING_RESULT[text as keyof typeof DISPLAY_SAMPLING_RESULT] ?? text}</Text>,
      },
      {
        title: "Unit",
        key: "unit",
        render: () => <Text>{UnitAllEnum.KG}</Text>,
      },
      ...getColumns(),
    ],
    [dataDisplay, getColumns],
  );

  return (
    <Modal
      title={<ModalTitle title="Fill Sampling Result" />}
      open={visibleModal}
      width={1300}
      onCancel={closeModal}
      footer={
        <Row className="w-full border-t" justify={"end"}>
          <Button className="mt-4" key="submit" type="primary" loading={loadingManageFillEmission} onClick={submitFillEmission}>
            Save
          </Button>
        </Row>
      }
      maskClosable={false}
      centered
    >
      {errorFillEmission && (
        <Row className="w-full" justify={"center"}>
          <Text type="danger">{errorFillEmission}</Text>
        </Row>
      )}
      <Row className="w-full mt-4" gutter={[12, 0]} justify={"center"}>
        <Col xs={12} sm={6} md={6}>
          <Text>Source Name</Text>
          <Input className="mt-2" disabled value={dataDisplay?.[0]?.sourceName} />
        </Col>
        <Col xs={12} sm={6} md={6}>
          <Text>Area</Text>
          <Input className="mt-2" disabled value={getMasterLabel("area", dataDisplay?.[0]?.area as string)} />
        </Col>
      </Row>
      <br></br>

      <Table
        key={JSON.stringify(dataDisplay)}
        // id="wrap-manual-fill-emission-table-config"
        // rowClassName={getRowClassName}
        style={{
          backgroundColor: "transparent",
        }}
        components={{
          header: {
            cell: (props: any) => (
              <th {...props} style={{ backgroundColor: "transparent", border: "none" }}>
                {props.children}
              </th>
            ),
          },
        }}
        columns={columns}
        scroll={{ x: "max-content" }}
        size={"small"}
        dataSource={dataDisplay ?? []}
        pagination={false}
      />
    </Modal>
  );
};

export default FillSamplingResultModal;
